/**
 * Invoices — the ledger of money in and out, plus the one place an invoice can
 * be raised by hand.
 *
 * The download is a plain link rather than an XHR: the endpoint streams a file
 * and axios would hand back a blob this panel has no reason to hold. See
 * `invoiceDownloadUrl` in admin-api.ts for the caveat that comes with it.
 */
import { generateInvoice, invoiceDownloadUrl, listInvoices } from '../admin-api';
import type { AdminUserRow, InvoiceRow, Paged } from '../admin-api.types';
import { mountPlayerSearch } from '../player-search';
import {
  badge, errorText, esc, field, fmtCents, fmtDate, pager, readForm, section, table,
} from '../admin-kit';
import type { AdminTab } from '../admin-kit';

export const invoicesTab: AdminTab = {
  id: 'invoices',
  label: 'Invoices',
  group: 'Billing',

  mount(host, ctx) {
    let live = true;
    let pageNo = 1;
    let search = '';
    let days: number | undefined;
    let data: Paged<InvoiceRow> | null = null;
    let error = '';
    let composing = false;
    /**
     * The chosen player, held out here rather than read off the form.
     *
     * The composer is part of the string `paint()` rewrites, so the picker is
     * destroyed and rebuilt whenever the list reloads underneath it. Keeping
     * the selection in the tab is what lets it be handed back to the new
     * widget instead of quietly resetting to nobody mid-form.
     */
    let player: AdminUserRow | null = null;
    let pickerTeardown: (() => void) | null = null;

    function composer(): string {
      if (!composing) return '';
      return `
        <form class="tg-adm-form" data-form="generate">
          <div class="tg-adm-form-row">
            <label class="tg-adm-field" style="flex:1 1 320px">
              <span class="tg-adm-label">Player</span>
              <div data-player-search></div>
              <span class="tg-adm-hint">Search by name, email or ID — results appear from the
                third character.</span>
            </label>
            <label class="tg-adm-field" style="flex:0 0 130px">
              <span class="tg-adm-label">Direction</span>
              <select name="type">
                <option value="IN">IN (player paid us)</option>
                <option value="OUT">OUT (we paid player)</option>
              </select>
            </label>
            ${field('amountCents', 'Amount in cents', '', { type: 'number', placeholder: '1999' })}
            ${field('currency', 'Currency', 'EUR')}
          </div>
          <div class="tg-adm-form-row">
            ${field('description', 'Description', '')}
            ${field('paymentMethod', 'Payment method', '')}
            ${field('vatRate', 'VAT %', '', { type: 'number' })}
          </div>
          <div class="tg-adm-form-row">
            ${field('recipientName', 'Recipient name', '')}
            ${field('recipientCompany', 'Recipient company', '')}
            ${field('recipientCode', 'Recipient code', '')}
          </div>
          <div class="tg-adm-actions">
            <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Raise invoice</button>
            <button type="button" class="tg-adm-btn" data-act="cancel-compose">Cancel</button>
          </div>
        </form>`;
    }

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!data) return '<p class="tg-adm-empty">Loading…</p>';
      return `
        ${composer()}
        ${table(
    data.items,
    [
      { label: 'Number', cell: (r) => `<span class="tg-adm-mono">${esc(r.number)}</span>`, tight: true },
      {
        label: 'Player',
        cell: (r) => `<strong>${esc(r.user?.username || '—')}</strong><br>
          <span class="tg-adm-mono">${esc(r.user?.email ?? '')}</span>`,
      },
      { label: 'Direction', cell: (r) => badge(r.type, r.type === 'IN' ? 'ok' : 'warn'), tight: true },
      { label: 'Amount', cell: (r) => esc(fmtCents(r.amountCents, r.currency)), tight: true },
      { label: 'For', cell: (r) => esc(r.description || '—') },
      { label: 'Method', cell: (r) => esc(r.paymentMethod || '—'), tight: true },
      { label: 'Raised', cell: (r) => esc(fmtDate(r.createdAt)), tight: true },
      {
        label: '',
        tight: true,
        cell: (r) => `<a class="tg-adm-btn" href="${esc(invoiceDownloadUrl(r.number))}"
          target="_blank" rel="noopener noreferrer">PDF</a>`,
      },
    ],
    'No invoices match.',
  )}
        ${pager(data.page, data.pageSize, data.total)}`;
    }

    host.innerHTML = section(
      'Invoices',
      'Every payment in and payout out, and the place to raise one by hand.',
      '<p class="tg-adm-empty">Loading…</p>',
      `<input data-search placeholder="Number or email" style="min-width:200px">
       <select data-days>
         <option value="">All time</option>
         <option value="7">Last 7 days</option>
         <option value="30">Last 30 days</option>
         <option value="365">Last year</option>
       </select>
       <button type="button" class="tg-adm-btn" data-act="reload">Search</button>
       <button type="button" class="tg-adm-btn tg-adm-btn-primary" data-act="compose">New invoice</button>`,
    );
    const bodyEl = host.querySelector<HTMLElement>('.tg-adm-body')!;
    const searchInput = host.querySelector<HTMLInputElement>('[data-search]')!;
    const daysSelect = host.querySelector<HTMLSelectElement>('[data-days]')!;

    const paint = () => {
      // Before the innerHTML, not after: the widget holds a document-level
      // listener and a pending timer, and the element it was mounted on is
      // about to stop existing.
      pickerTeardown?.();
      pickerTeardown = null;
      bodyEl.innerHTML = body();
      const slot = bodyEl.querySelector<HTMLElement>('[data-player-search]');
      if (slot) {
        pickerTeardown = mountPlayerSearch(slot, {
          selected: player,
          onPick: (user) => { player = user; },
        });
      }
    };

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listInvoices({ page: pageNo, search: search || undefined, days });
        if (!live) return;
        data = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-page]');
      if (!el) return;
      if (el.dataset.page) { pageNo = Number(el.dataset.page); void load(); return; }
      switch (el.dataset.act) {
        case 'reload':
          pageNo = 1;
          search = searchInput.value.trim();
          days = daysSelect.value ? Number(daysSelect.value) : undefined;
          void load();
          break;
        case 'compose':
          composing = true;
          paint();
          break;
        case 'cancel-compose':
          composing = false;
          player = null;
          paint();
          break;
        default:
          break;
      }
    };

    const onSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      if (form.dataset.form !== 'generate') return;
      e.preventDefault();
      const v = readForm(form);
      const amountCents = Number(v.amountCents);
      if (!player) { ctx.notify('Choose a player first.', 'err'); return; }
      if (!Number.isInteger(amountCents) || amountCents <= 0) {
        ctx.notify('Amount must be a whole number of cents above zero.', 'err');
        return;
      }
      void (async () => {
        try {
          const invoice = await generateInvoice({
            userId: player.id,
            type: v.type as 'IN' | 'OUT',
            amountCents,
            currency: String(v.currency || '') || undefined,
            description: String(v.description || '') || undefined,
            paymentMethod: String(v.paymentMethod || '') || undefined,
            vatRate: v.vatRate ? Number(v.vatRate) : undefined,
            recipientName: String(v.recipientName || '') || undefined,
            recipientCompany: String(v.recipientCompany || '') || undefined,
            recipientCode: String(v.recipientCode || '') || undefined,
          });
          if (!live) return;
          composing = false;
          player = null;
          ctx.notify(`Invoice ${invoice.number} raised.`);
        } catch (err) {
          if (!live) return;
          ctx.notify(errorText(err), 'err');
          return;
        }
        await load();
      })();
    };

    host.addEventListener('click', onClick);
    host.addEventListener('submit', onSubmit as EventListener);

    void load();

    return () => {
      live = false;
      pickerTeardown?.();
      pickerTeardown = null;
      host.removeEventListener('click', onClick);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
